import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import type { RootState } from '../../../../redux/Store';
import {
  setFarmYearPracticeTags,
  setFarmYearRainfall,
  type PracticeTag,
} from '../../../../redux/slices/InsightsSlice';
import { storeFarmYearInput } from './farmYearInputsStorage';

/**
 * Sets one farm's rainfall or management practice for a year, and remembers
 * the resulting entry in this browser so it survives a refresh or a later
 * visit (OP-2528). Every edit to these fields goes through here.
 */
export const useFarmYearInputActions = (farmId: string) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const remember = useCallback(
    (year: number) =>
      storeFarmYearInput(
        farmId,
        year,
        store.getState().insightsReducer.farmYearInputs[farmId]?.[year]
      ),
    [farmId, store]
  );

  const setRainfall = useCallback(
    (year: number, rainfallMm: number | null) => {
      dispatch(setFarmYearRainfall({ farmId, year, rainfallMm }));
      remember(year);
    },
    [dispatch, farmId, remember]
  );

  const setPracticeTags = useCallback(
    (year: number, tags: PracticeTag[]) => {
      dispatch(setFarmYearPracticeTags({ farmId, year, tags }));
      remember(year);
    },
    [dispatch, farmId, remember]
  );

  return { setRainfall, setPracticeTags };
};
